import { useRouter } from 'next/router';
import { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, ChevronRight, ChevronLeft } from "lucide-react";
import { motion } from 'framer-motion';

import { getContent } from '@/utils/helper';
import { analytics, getClickHeaderNavigation, getClickCtaDetail, getClickNavigationExternalCtaDetail, getClickNavigationSubMenuToggle } from '@/utils/analytics';

import Icon from '@/atoms/Icon/Icon';
import Text from '@/atoms/Text/Text';
import CtfCta, { CtaWrapper, Cta } from '@/molecules/CTA/index';
import { DynamicAsset } from '@/molecules/Media/index';
import { CtfSubNavigationStyle } from '@/utils/constants';
import Button from '@/atoms/Button/Button';

function NavigationWithSubMenu({
  entries = [],
  navigationMenu = [],
  closeNavigation = () => {},
  setShowCta,
  screen,
  style = '',
}) {
  const router = useRouter();
  const { asPath, locale, defaultLocale } = router;
  const [isHover, toggleHover] = useState(false);
  const [menuIndex, setMenuIndex] = useState(null);
  const [subMenuIndex, setSubMenuIndex] = useState(0);
  const [mobileMenu, setMobileMenu] = useState("menu");
  const toggleHoverMenu = (e) => {
    setMenuIndex(e.target.id);
    toggleHover(true);
  };

  const subMenuAnimate = {
    enter: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
      display: "flex",
    },
    exit: {
      opacity: 0,
      transition: {
        duration: 0.5,
      },
      transitionEnd: {
        display: "none",
      },
    },
  };

  const subMenuMobileAnimate = {
    enter: {
      display: "flex",
    },
    exit: {
      transitionEnd: {
        display: "none",
      },
    },
  }

  const handleMenuClick = (e, url) => {
    analytics({
      window,
      document,
      detail: getClickHeaderNavigation(e, { url }),
    });
    closeNavigation();
    if (locale == defaultLocale) {
      window.location.href = url;
    } else {
      window.location.href = `/${locale}${url}`
    }
  };

  const getCtaAnalytics = (e, data) => {
    analytics({
      window,
      document,
      detail: data?.localUrl
        ? getClickCtaDetail(e, data)
        : getClickNavigationExternalCtaDetail(e, data),
    });
  };

  const handleNavigationSubMenuToggle = (e, { link_text, link_classes }) => {
    analytics({
      window,
      document,
      detail: getClickNavigationSubMenuToggle(e, {
        link_text,
        link_classes,
        lang_country: locale
      }),
    })
  }

  const TextLinks = ({ index, title, image, callToAction }) => {
    return (
      <div className="w-full col-span-1" key={index}>
        <div className="rounded-[8px] overflow-hidden border-[1px] border-actual-dd-gold">
          <DynamicAsset
            assets={image}
            entries={entries}
            field={image}
            container="container"
            style="aspect-[33/22] md:aspect-[433/293] lg:aspect-[109/76]"
          />
        </div>
        <Text tag="h6" style="uppercase text-progressive-blue mt-10">
          {title}
        </Text>
        {callToAction && callToAction.map((item, index) => {
          return (
            <div key={index} className="mt-[10px]">
              <CtfCta
                field={item}
                entries={entries}
                onClick={getCtaAnalytics}
                animation="hover:underline"
                // wrapperClassName=""
                hideIcon
              />
            </div>
          )
        })}
      </div>
    )
  }

  const ImageBanner = ({ index, title, image, callToAction }) => {
    return (
      <div className="relative inline-flex w-full col-span-2" key={index}>
        <div className="rounded-[8px] z-1 overflow-hidden border-[1px] border-actual-dd-gold" style={{ zIndex: 0}}>
          <DynamicAsset
            assets={image}
            entries={entries}
            field={image}
            container="container"
            style="aspect-[453/280]"
          />
        </div>
        <div className="absolute flex flex-col w-full h-full px-[30px] pt-[83px] pb-[30px] justify-center items-center" style={{ zIndex: 1 }}>
          <Text tag="h5" style="uppercase text-white">
            {title}
          </Text>
          {callToAction && callToAction.map((item, index) => {
            if (index < 1) {
              return (
                <div key={index} className="mt-[25px]">
                  <CtfCta
                    field={item}
                    entries={entries}
                    onClick={getCtaAnalytics}
                    animation="hover:bg-quality-gold"
                    hideIcon
                  />
                </div>
              )
            } else {
              return;
            }
          })}
        </div>
      </div>
    )
  }

  const FourImagesTextLinks = ({ subNavigationHeader, subNavigationCta, subNavigation }) => {
    return (
      <>
        <div className="relative flex flex-col shrink-0 w-[230px]">
          <Text tag="h3" style="uppercase max-w-[230px] text-progressive-blue !leading-[59px]">
            {subNavigationHeader?.[0]}
          </Text>
          {subNavigationCta && subNavigationCta.map((item, index) => {
            return (
              <div key={index} className={'flex item-center mt-[30px]'}>
                <CtfCta
                  field={item}
                  entries={entries}
                  onClick={getCtaAnalytics}
                  animation="hover:underline"
                />
              </div>
            )
          })}
        </div>
        <div className="relative flex flex-auto gap-x-[20px]">
          {subNavigation &&
            subNavigation.map((item, index) => {
              const content = getContent(item.sys?.id, entries);
              let {
                title,
                image,
                callToAction
              } = content?.fields || {};

              if (index < 4) {
                return (
                  <TextLinks
                    key={item.sys?.id || index}
                    index={index}
                    title={title}
                    image={image}
                    callToAction={callToAction} />
                )
              } else {
                return;
              }
            })
          }
        </div>
      </>
    )
  }

  const TwoImagesTextLinksImageBanner = ({ subNavigationHeader, subNavigationCta, subNavigation }) => {
    return (
      <>
        <div className="relative flex flex-col shrink-0 w-[230px]">
          <Text tag="h3" style="uppercase max-w-[230px] text-progressive-blue !leading-[59px]">
            {subNavigationHeader?.[0]}
          </Text>
          {subNavigationCta && subNavigationCta.map((item, index) => {
            return (
              <div key={index} className={'flex item-center mt-[30px]'}>
                <CtfCta
                  field={item}
                  entries={entries}
                  onClick={getCtaAnalytics}
                  animation="hover:underline"
                />
              </div>
            )
          })}
        </div>
        <div className="relative grid grid-cols-4 gap-x-[20px]">
          {subNavigation &&
            subNavigation.map((item, index) => {
              const content = getContent(item.sys?.id, entries);
              let {
                title,
                subNavigationHeader,
                image,
                callToAction
              } = content?.fields || {};

              if (index < 2) {
                return (
                  <TextLinks
                    key={item.sys?.id || index}
                    index={index}
                    title={title}
                    image={image}
                    callToAction={callToAction} />
                )
              } else if (index == 2) {
                return (
                  <ImageBanner
                    key={item.sys?.id || index}
                    index={index}
                    title={title}
                    image={image}
                    callToAction={callToAction} />
                )
              } else {
                return;
              }
            })
          }
        </div>
      </>
    )
  }

  const FourImagesTextLinksSubMenu = ({ subNavigationHeader, subNavigationCta, subNavigation }) => {
    return (
      <div className="relative flex flex-col w-full">
        <div className="flex flex-row w-full justify-between items-center">
          <div className="flex flex-row gap-x-[36px]">
            {subNavigationHeader && subNavigationHeader.map((item, index) => {
              return (
                <div
                  key={index}
                  className="aaco-underline aaco-underline--thick aaco-underline--actual-dd-gold"
                  onClick={(e) => {
                    setSubMenuIndex(index)
                    handleNavigationSubMenuToggle(e, {
                      link_text: item,
                      link_classes: e.target.classList.value,
                    })
                  }}
                >
                  <Text tag="h3" style={`cursor-pointer ${subMenuIndex === index ? "text-progressive-blue" : "text-light-grey-2"} hover:text-progressive-blue`}>
                    {item}
                  </Text>
                </div>
              )
            })}
          </div>
          {subNavigationCta && subNavigationCta.map((item, index) => {
            if (subMenuIndex == index) {
              return (
                <div key={index} className={'flex item-center'}>
                  <CtfCta
                    field={item}
                    entries={entries}
                    onClick={getCtaAnalytics}
                  />
                </div>
              )
            } else {
              return;
            }
          })}
        </div>
        <div className="flex flex-row gap-x-[20px] mt-[35px]">
          {subNavigation &&
            subNavigation.map((item, index) => {
              const content = getContent(item.sys?.id, entries);
              let {
                title,
                image,
                callToAction
              } = content?.fields || {};

              if (content?.fields.subNavigationHeader == subNavigationHeader?.[subMenuIndex]) {
                return (
                  <div className="flex flex-col w-full" key={index}>
                    <div className="rounded-[8px] overflow-hidden border-[1px] border-actual-dd-gold">
                      <DynamicAsset
                        assets={image}
                        entries={entries}
                        field={image}
                        container="container"
                        style="aspect-[305/212]"
                      />
                    </div>
                    <Text tag="h6" style="uppercase text-progressive-blue mt-10">
                      {title}
                    </Text>
                    {callToAction && callToAction.map((item, index) => {
                      return (
                        <div key={index} className="mt-[10px]">
                          <CtfCta
                            field={item}
                            entries={entries}
                            onClick={getCtaAnalytics}
                            wrapperClassName="aaco-underline aaco-underline--actual-dd-gold"
                            hideIcon
                          />
                        </div>
                      )
                    })}
                  </div>
                )
              } else {
                return;
              }
            })
          }
        </div>
      </div>
    )
  }

  const TwoImageBanner = ({ subNavigationHeader, subNavigationCta, subNavigation }) => {
    return (
      <>
        <div className="relative flex flex-col shrink-0 w-[271px]">
          <Text tag="h3" style="uppercase max-w-[271px] text-progressive-blue !leading-[59px]">
            {subNavigationHeader?.[0]}
          </Text>
          {subNavigationCta && subNavigationCta.map((item, index) => {
            return (
              <div key={index} className={'mt-[30px]'}>
                <CtfCta
                  field={item}
                  entries={entries}
                  onClick={getCtaAnalytics}
                />
              </div>
            )
          })}
        </div>
        <div className="relative w-full grid grid-cols-4 gap-x-[20px]">
          {subNavigation &&
            subNavigation.map((item, index) => {
              const content = getContent(item.sys?.id, entries);
              let {
                title,
                image,
                callToAction
              } = content?.fields || {};

              if (index < 2) {
                return (
                  <ImageBanner
                    key={item.sys?.id || index}
                    index={index}
                    title={title}
                    image={image}
                    callToAction={callToAction} />
                )
              } else {
                return;
              }
            })
          }
        </div>
      </>
    )
  }

  return (
    <ul className={`aaco-menus nav-menu__items w-full lg:h-auto lg:items-center justify-start lg:ml-[71px] gap-x-10 ${style}`}>
      {navigationMenu.length > 0 &&
        navigationMenu.slice(0, navigationMenu.length - 2).map((m, index) => {
          const { subNavigationStyle, subNavigationHeader, subNavigationCta, subNavigation } = m;

          if (!m?.subNavigationStyle) return (
            <motion.li
              key={index}
              className="h-[55px] lg:h-auto px-[30px] py-[15px] lg:p-0"
              animate={mobileMenu !== "menu" ? "exit" : "enter"}
              variants={subMenuMobileAnimate}
            >
              <CtaWrapper
                url={m?.url}
                page={m?.pageUrl}
                onClick={(e) =>
                  handleMenuClick(e, m?.url ?? `/${m?.pageUrl}`)
                }
                classNames={"aaco-underline aaco-underline--natural-cream"}
              >
                <Text tag="body" style={`${`/${m?.pageUrl}` === asPath ? "font-bold" : ""}`}>
                  {m.name}
                </Text>
              </CtaWrapper>
            </motion.li>
          );

          if (screen === "mobile") {
            return (
              <Fragment key={index}>
                <li key={index} className={`group/link aaco-navigation__link px-[30px] py-[15px] w-full h-[55px] ${mobileMenu == "menu" ? "flex" : "hidden"} cursor-pointer`}>
                  <Button
                    key={index}
                    style="flex flex-row items-center cursor-pointer w-full"
                    onClick={() => {
                      setShowCta(false)
                      setMobileMenu(m.name)
                    }}
                  >
                    <Text tag="body" style={`${`/${m?.pageUrl}` === asPath ? "font-bold" : ""} mr-auto`}>
                      {m.name}
                    </Text>
                    <ChevronRight className="text-natural-cream ml-auto" />
                  </Button>
                </li>
                <div className={`${mobileMenu == m.name ? "flex" : "hidden"} bg-natural-cream w-full h-auto p-[30px] gap-x-[90px] flex flex-col`}>
                  <div className="flex flex-col gap-[30px] mb-10">
                    <Button
                      key={index}
                      style="flex flex-row items-center cursor-pointer"
                      onClick={() => {
                        setShowCta(true)
                        setMobileMenu("menu")
                      }}
                    >
                      <ChevronLeft className="text-progressive-blue mr-[4px]" />
                      <Text tag="body" style="text-progressive-blue">
                        Back
                      </Text>
                    </Button>
                    {subNavigationStyle === CtfSubNavigationStyle.FOUR_IMAGES_TEXT_LINKS_SUB_MENU ? (
                      <>
                        <div className="flex flex-row gap-[22px]">
                          {subNavigationHeader && subNavigationHeader.map((item, index) => {
                            return (
                              <div
                                key={index}
                                className="aaco-underline aaco-underline--actual-dd-gold"
                                onClick={(e) => {
                                  setSubMenuIndex(index)
                                  handleNavigationSubMenuToggle(e, {
                                    link_text: item,
                                    link_classes: e.target.classList.value,
                                  })
                                }}
                              >
                                <Text tag="h3" style={`cursor-pointer ${subMenuIndex === index ? "text-progressive-blue" : "text-light-grey-2"} hover:text-progressive-blue`}>
                                  {item}
                                </Text>
                              </div>
                            )
                          })}
                        </div>
                        {subNavigationCta && subNavigationCta.map((item, index) => {
                          if (subMenuIndex === index) {
                            return (
                              <div key={index} className={'flex items-center'}>
                                <CtfCta
                                  field={item}
                                  entries={entries}
                                  onClick={getCtaAnalytics}
                                />
                              </div>
                            )
                          }
                        })}
                      </>
                    ) : (
                      <>
                        <Text tag="h3" style="uppercase text-progressive-blue">
                          {subNavigationHeader?.[0]}
                        </Text>
                        {subNavigationCta && subNavigationCta.map((item, index) => {
                          return (
                            <div key={index} className={'flex items-center'}>
                              <CtfCta
                                field={item}
                                entries={entries}
                                onClick={getCtaAnalytics}
                              />
                            </div>
                          )
                        })}

                      </>
                    )}
                  </div>
                  <div className="flex flex-col gap-[60px] md:grid md:grid-cols-2 md:gap-[20px]">
                    {subNavigation &&
                      subNavigation.map((item, index) => {
                        const content = getContent(item.sys?.id, entries);
                        let {
                          title,
                          image,
                          callToAction
                        } = content?.fields || {};

                        if (subNavigationStyle === CtfSubNavigationStyle.FOUR_IMAGES_TEXT_LINKS && index < 4) {
                          return (
                            <TextLinks
                              key={item.sys?.id || index}
                              index={index}
                              title={title}
                              image={image}
                              callToAction={callToAction} />
                          )
                        } else if (subNavigationStyle === CtfSubNavigationStyle.TWO_IMAGES_TEXT_LINKS_IMAGE_BANNER && index < 2) {
                          return (
                            <TextLinks
                              key={item.sys?.id || index}
                              index={index}
                              title={title}
                              image={image}
                              callToAction={callToAction} />
                          )
                        } else if (subNavigationStyle === CtfSubNavigationStyle.TWO_IMAGES_TEXT_LINKS_IMAGE_BANNER && index == 2) {
                          return (
                            <ImageBanner
                              key={item.sys?.id || index}
                              index={index}
                              title={title}
                              image={image}
                              callToAction={callToAction} />
                          )
                        } else if (subNavigationStyle === CtfSubNavigationStyle.FOUR_IMAGES_TEXT_LINKS_SUB_MENU && content?.fields.subNavigationHeader == subNavigationHeader?.[subMenuIndex]) {
                          return (
                            <div className="flex flex-col w-full" key={index}>
                              <div className="rounded-[8px] overflow-hidden border-[1px] border-actual-dd-gold">
                                <DynamicAsset
                                  assets={image}
                                  entries={entries}
                                  field={image}
                                  container="container"
                                  style="aspect-[305/212]"
                                />
                              </div>
                              <Text tag="h6" style="uppercase text-progressive-blue mt-10">
                                {title}
                              </Text>
                              {callToAction && callToAction.map((item, index) => {
                                return (
                                  <div key={index} className="mt-[10px]">
                                    <CtfCta
                                      field={item}
                                      entries={entries}
                                      onClick={getCtaAnalytics}
                                      wrapperClassName="aaco-underline aaco-underline--actual-dd-gold"
                                      hideIcon
                                    />
                                  </div>
                                )
                              })}
                            </div>
                          )
                        } else if (subNavigationStyle === CtfSubNavigationStyle.TWO_IMAGE_BANNERS && index < 2) {
                          return (
                            <ImageBanner
                              key={item.sys?.id || index}
                              index={index}
                              title={title}
                              image={image}
                              callToAction={callToAction} />
                          )
                        }
                      })
                    }
                  </div>
                </div>
              </Fragment>
            )
          }

          return (
            <motion.li
              id={`aaco-nav-menu ${index}`}
              className="group/link aaco-navigation__link py-[15px] lg:p-0"
              onHoverStart={(e) => {
                toggleHoverMenu(e);
              }}
              onHoverEnd={() => toggleHover(false)}
              key={index}
            >
              <div className='flex flex-row'>
                <CtaWrapper
                  url={m?.url}
                  page={m?.pageUrl}
                  onClick={(e) =>
                    handleMenuClick(e, m?.url ?? `/${m?.pageUrl}`)
                  }
                  classNames={"aaco-underline aaco-underline--natural-cream"}
                >
                  <Text tag="body" style={`${`/${m?.pageUrl}` === asPath ? "font-bold" : ""}`}>
                    {m.name}
                  </Text>
                </CtaWrapper>
                <ChevronDown className="text-natural-cream ml-2" />
              </div>
              <motion.div
                className="sub-menu absolute flex top-[100px] left-0 bg-natural-cream w-full min-h-[442px] px-[90px] py-[80px] gap-x-[90px]"
                initial="exit"
                animate={isHover && `aaco-nav-menu ${index}` == menuIndex ? "enter" : "exit"}
                variants={subMenuAnimate}
              >
                {subNavigationStyle === CtfSubNavigationStyle.FOUR_IMAGES_TEXT_LINKS ? (
                  <FourImagesTextLinks
                    subNavigationHeader={subNavigationHeader}
                    subNavigationCta={subNavigationCta}
                    subNavigation={subNavigation} />
                ) : subNavigationStyle === CtfSubNavigationStyle.TWO_IMAGES_TEXT_LINKS_IMAGE_BANNER ? (
                  <TwoImagesTextLinksImageBanner
                    subNavigationHeader={subNavigationHeader}
                    subNavigationCta={subNavigationCta}
                    subNavigation={subNavigation} />
                ) : subNavigationStyle === CtfSubNavigationStyle.FOUR_IMAGES_TEXT_LINKS_SUB_MENU ? (
                  <FourImagesTextLinksSubMenu
                    subNavigationHeader={subNavigationHeader}
                    subNavigationCta={subNavigationCta}
                    subNavigation={subNavigation} />
                ) : subNavigationStyle === CtfSubNavigationStyle.TWO_IMAGE_BANNERS ? (
                  <TwoImageBanner
                    subNavigationHeader={subNavigationHeader}
                    subNavigationCta={subNavigationCta}
                    subNavigation={subNavigation} />
                ) : null}
              </motion.div>
            </motion.li>
          );
        })}
    </ul>
  );
}

NavigationWithSubMenu.propTypes = {
  entries: PropTypes.array,
  navigationMenu: PropTypes.array,
  closeNavigation: PropTypes.func,
  style: PropTypes.string,
};

export default NavigationWithSubMenu;
